import { usePlanningSession } from '../../contexts/planningSession';
import { serviceScope } from '../../lib/planningCycles';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useI18n } from '../../contexts/I18nContext';
import { AccountMenu } from './AccountMenu';
import { getVisibleNavigationItems } from './navigationItems';

export const Navigation: React.FC = () => {
  const location = useLocation();
  const session = usePlanningSession();
  const destination = (href: string) =>
    session?.cycle &&
    ['/plan-cycles', '/midweek-meeting-planning', '/service-planning', '/field-service'].includes(
      href
    )
      ? `${href}?scopeId=${href === '/service-planning' ? serviceScope(session.cycle) : session.cycle}`
      : href;
  const { user, hasAnyPermission } = useAuth();
  const { t } = useI18n();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigation = getVisibleNavigationItems(hasAnyPermission, user?.appRole);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className="bg-white shadow">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex min-h-16 flex-wrap items-center justify-between gap-3 py-3">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex-shrink-0">
              <Link
                to="/dashboard"
                onClick={event => {
                  if (session?.blocked) event.preventDefault();
                }}
                aria-disabled={session?.blocked || undefined}
                className="rounded text-xl font-bold text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-500"
                aria-current={location.pathname === '/dashboard' ? 'page' : undefined}
              >
                {t('common.appName')}
              </Link>
            </div>
            <div className="hidden xl:flex xl:flex-wrap xl:items-center xl:gap-x-6 xl:gap-y-2">
              {navigation.map(item => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.key}
                    to={destination(item.href)}
                    onClick={event => {
                      if (session?.blocked) event.preventDefault();
                    }}
                    aria-disabled={session?.blocked || undefined}
                    className={cn(
                      'inline-flex items-center border-b-2 px-1 py-1 text-sm font-medium',
                      isActive
                        ? 'border-primary-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    )}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {t(`nav.${item.key}`)}
                  </Link>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(open => !open)}
              className="inline-flex items-center h-11 w-11 justify-center rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 xl:hidden"
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <AccountMenu key={location.key} />
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={cn('border-t border-gray-200 xl:hidden', isMobileMenuOpen ? 'block' : 'hidden')}
      >
        <div className="space-y-1 px-4 pb-4 pt-2 sm:px-6 lg:px-8">
          {navigation.map(item => {
            const Icon = item.icon;
            const isActive = location.pathname === item.href;

            return (
              <Link
                key={item.key}
                to={destination(item.href)}
                onClick={event => {
                  if (session?.blocked) event.preventDefault();
                }}
                aria-disabled={session?.blocked || undefined}
                className={cn(
                  'block rounded-md px-3 py-2 text-base font-medium',
                  isActive
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                )}
              >
                <div className="flex items-center">
                  <Icon className="h-4 w-4 mr-3" />
                  {t(`nav.${item.key}`)}
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};
